import React, { useState, useEffect } from "react";
import withRouter from "../Common/withRouter";
import { Layout, Menu, Button, Drawer } from "antd";
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  MessageSquare,
  Users,
  Shield,
  ShieldCheck,
  LogOut,
  DollarSign,
  CreditCard,
  UserCheck,
  Linkedin,
  Building2,
  ListChecks,
  RotateCcw,
  FileText,
  Mail,
  Twitter,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../assets/scss/dashboard_theme.scss";
import { supabase, invokeEdgeFunction } from "../services/supabase";
import {
  SIDEBAR_BADGE_REFRESH,
  SIDEBAR_BADGE_ADJUST,
} from "../services/sidebarBadges";

const iconProps = { size: 18, strokeWidth: 2.5 };

const SECTION_BY_PATH = {
  "/submitted-tells": "submitted_tells",
  "/brand-enquiries": "user_enquiries",
  "/content-moderation": "flagged_mismatches",
};

const SidebarNavLabel = ({ to, label, count, variant }) => (
  <Link to={to} className="sidebar-nav-link">
    <span className="sidebar-nav-label">{label}</span>
    {count > 0 ? (
      <span className={`sidebar-nav-badge sidebar-nav-badge--${variant}`}>
        {count > 99 ? "99+" : count}
      </span>
    ) : null}
  </Link>
);

const { Sider } = Layout;

const SidebarLayout = ({ theme, drawerOpen, setDrawerOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const pathnameRef = React.useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // RBAC State
  const [systemRole, setSystemRole] = useState(localStorage.getItem("admin_system_role") || "moderator");
  const [counts, setCounts] = useState({
    submitted_tells_unread: 0,
    user_enquiries_unread: 0,
    flagged_mismatches_unread: 0,
  });
  const [allowedSections, setAllowedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("admin_allowed_sections") || "[]");
    } catch {
      return [];
    }
  });

  const SECTION_ROUTE_MAP = {
    // Teller Ecosystem
    route_users: ["/users"],
    route_brand_enquiries: ["/brand-enquiries"],
    route_dispatch_desk: ["/dispatch-desk"],
    route_verified_dir: ["/verified-directory"],
    route_submitted_tells: ["/submitted-tells"],
    route_moderation: ["/content-moderation"],
    route_intelligence: ["/intelligence-feed"],
    // System Core
    route_revenue: ["/payment/revenue"],
    route_refunds: ["/payment/refunds"],
    route_queue: ["/queue-control"],
    route_sub_tiers: ["/payment/subscriptions-plans"],
    route_brand_status: ["/brand-status", "/linkedin-dispatch"],
    route_linkedin: ["/linkedin-dispatch"],
    // Operations
    route_brand_mgmt: ["/brand-management", "/brand-claims"],
    route_cms: ["/admin/cms-pages"],
    route_email_templates: ["/admin/email-templates"],
    route_staff_access: ["/admin/staff-access"],
    route_user_management: ["/users", "/brand-enquiries"],
    route_growth_leads: ["/admin/growth/twitter-leads"],
    route_master_panel: ["/master-panel"],
  };

  const isRouteAllowed = (path) => {
    const section = Object.keys(SECTION_ROUTE_MAP).find(secId =>
      SECTION_ROUTE_MAP[secId].includes(path)
    );
    if (!section) return true; // Public or non-gated route

    return systemRole === "super_admin" || allowedSections.includes(section);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const antHeaderMain = document.getElementById("antHeaderMain");
    const antLayoutContent = document.getElementById("antLayoutContent");
    const antFooterLayout = document.getElementById("antFooterLayout");

    const leftPos = isMobile ? "0px" : (collapsed ? "100px" : "280px");

    if (antHeaderMain) antHeaderMain.style.left = leftPos;
    if (antLayoutContent) antLayoutContent.style.marginLeft = leftPos;
    if (antFooterLayout) antFooterLayout.style.marginLeft = leftPos;
  }, [collapsed, isMobile]);



  useEffect(() => {
    let cancelled = false;

    const fetchSidebarCounts = async () => {
      try {
        const { data, error } = await invokeEdgeFunction("admin-management", {
          body: { action: "get_sidebar_badge_counts" },
        });
        if (cancelled) return;
        if (error) {
          console.error("Failed to fetch sidebar notification counts", error);
          return;
        }
        const payload = data?.data || {};
        if (data?.status === "success" || data?.success) {
          const path = pathnameRef.current;
          setCounts({
            submitted_tells_unread: path === "/submitted-tells" ? 0 : (payload.submitted_tells_unread || 0),
            user_enquiries_unread: path === "/brand-enquiries" ? 0 : (payload.user_enquiries_unread || 0),
            flagged_mismatches_unread: path === "/content-moderation" ? 0 : (payload.flagged_mismatches_unread || 0),
          });
        }
      } catch (err) {
        console.error("Failed to fetch sidebar notification counts", err);
      }
    };

    fetchSidebarCounts();
    const interval = setInterval(fetchSidebarCounts, 30000);

    const onRefresh = () => fetchSidebarCounts();
    const onAdjust = (event) => {
      const { key, delta } = event.detail || {};
      if (!key || typeof delta !== "number") return;
      setCounts((prev) => ({
        ...prev,
        [key]: Math.max(0, (prev[key] || 0) + delta),
      }));
    };

    window.addEventListener(SIDEBAR_BADGE_REFRESH, onRefresh);
    window.addEventListener(SIDEBAR_BADGE_ADJUST, onAdjust);

    const channel = supabase
      .channel("admin-sidebar-badges")
      .on("postgres_changes", { event: "*", schema: "public", table: "tells_raw" }, fetchSidebarCounts)
      .on("postgres_changes", { event: "*", schema: "public", table: "brand_inquiries" }, fetchSidebarCounts)
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener(SIDEBAR_BADGE_REFRESH, onRefresh);
      window.removeEventListener(SIDEBAR_BADGE_ADJUST, onAdjust);
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    const section = SECTION_BY_PATH[location.pathname];
    if (!section) return;

    const countKey = `${section}_unread`;
    setCounts((prev) => (prev[countKey] ? { ...prev, [countKey]: 0 } : prev));

    (async () => {
      try {
        await invokeEdgeFunction("admin-management", {
          body: { action: "mark_admin_section_read", section },
        });
      } catch (err) {
        console.error("Failed to mark sidebar section as read", err);
      }
    })();
  }, [location.pathname]);

  // Periodic session validation (every 3 minutes & on window focus) to handle administrative revocation efficiently
  useEffect(() => {
    const adminSession = JSON.parse(localStorage.getItem('admin_session') || '{}');
    const userId = adminSession?.user?.id || adminSession?.session?.user?.id;
    if (!userId) return;

    let lastChecked = 0;

    const validateSession = async () => {
      // Prevent duplicate calls within 30 seconds
      if (Date.now() - lastChecked < 30000) return;
      lastChecked = Date.now();

      try {
        const telemetrySessionId = localStorage.getItem("admin_telemetry_session_id");
        const { data, error } = await supabase.functions.invoke("admin-management", {
          body: {
            action: "bootstrap",
            user_id: userId,
            is_new_login: false,
            telemetry_session_id: telemetrySessionId
          }
        });

        if (!error && data?.success && data?.user) {
          const role = data.user.system_role || "moderator";
          const sections = data.user.allowed_sections || [];

          setSystemRole(prev => (prev !== role ? role : prev));
          setAllowedSections(prev => {
            const nextJson = JSON.stringify(sections);
            return JSON.stringify(prev) !== nextJson ? sections : prev;
          });

          localStorage.setItem("admin_system_role", role);
          localStorage.setItem("admin_allowed_sections", JSON.stringify(sections));
          localStorage.setItem("admin_staff_id", data.user.staff_id || "");
        } else if (!error && data && data.success === false) {
          if (data.reason === 'suspended') {
            navigate("/logout?reason=suspended");
          } else {
            navigate("/logout?reason=force");
          }
        }
      } catch (err) {
        console.error("[SidebarLayout] Session validation failed:", err);
      }
    };

    // Check on window focus (when tab becomes active)
    window.addEventListener("focus", validateSession);

    // Periodic heartbeat every 15 seconds
    const interval = setInterval(validateSession, 15000);

    return () => {
      window.removeEventListener("focus", validateSession);
      clearInterval(interval);
    };
  }, [navigate]);

  // Gating route enforcement
  useEffect(() => {
    if (location.pathname === "/unauthorized") return;

    if (!isRouteAllowed(location.pathname)) {
      const hasRoutePermission = allowedSections.some(secId => secId in SECTION_ROUTE_MAP);
      if (systemRole !== "super_admin" && !hasRoutePermission) {
        navigate("/unauthorized", { replace: true });
      } else {
        // Redirect to their first allowed page, or /master-panel if they have that
        if (allowedSections.includes("route_master_panel")) {
          navigate("/master-panel", { replace: true });
        } else {
          const firstAllowedSec = Object.keys(SECTION_ROUTE_MAP).find(secId => allowedSections.includes(secId));
          if (firstAllowedSec) {
            navigate(SECTION_ROUTE_MAP[firstAllowedSec][0], { replace: true });
          } else {
            navigate("/unauthorized", { replace: true });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, allowedSections, systemRole, navigate]);

  const getItem = (label, key, icon, children, type, extra = {}) => {
    return {
      key,
      icon,
      children,
      label,
      type,
      ...extra,
    };
  };

  const roleNameMap = {
    super_admin: "Super Admin",
    moderator: "Triage Moderator",
    analyst: "Financial Analyst",
    compliance_officer: "Compliance Officer",
    user_management: "User Management",
    operations: "Operations Staff",
    revenue_staff: "Revenue Staff",
  };
  const roleDisplay = roleNameMap[systemRole] || "Staff Admin";

  const items = [
    getItem(
      <Link to="/master-panel">Master Panel</Link>,
      "/master-panel",
      <LayoutGrid {...iconProps} />
    ),

    getItem("Teller Ecosystem", "group-teller", null, [
      getItem(
        <Link to="/users">User Directory</Link>,
        "/users",
        <Users {...iconProps} />
      ),
      getItem(
        <SidebarNavLabel
          to="/brand-enquiries"
          label="Brand Enquiries"
          count={location.pathname === "/brand-enquiries" ? 0 : counts.user_enquiries_unread}
          variant="rose"
        />,
        "/brand-enquiries",
        <ListChecks {...iconProps} />,
        undefined,
        undefined,
        {
          className:
            location.pathname !== "/brand-enquiries" && counts.user_enquiries_unread > 0
              ? "sidebar-item-has-badge sidebar-item-has-badge--rose"
              : undefined,
        }
      ),
      getItem(
        <Link to="/dispatch-desk">Dispatch Desk</Link>,
        "/dispatch-desk",
        <Mail {...iconProps} />
      ),
      getItem(
        <Link to="/verified-directory">Verified Directory</Link>,
        "/verified-directory",
        <UserCheck {...iconProps} />
      ),
      getItem(
        <SidebarNavLabel
          to="/submitted-tells"
          label="Submitted Tells"
          count={location.pathname === "/submitted-tells" ? 0 : counts.submitted_tells_unread}
          variant="amber"
        />,
        "/submitted-tells",
        <MessageSquare {...iconProps} />,
        undefined,
        undefined,
        {
          className:
            location.pathname !== "/submitted-tells" && counts.submitted_tells_unread > 0
              ? "sidebar-item-has-badge sidebar-item-has-badge--amber"
              : undefined,
        }
      ),
      getItem(
        <SidebarNavLabel
          to="/content-moderation"
          label="Moderation Hub"
          count={location.pathname === "/content-moderation" ? 0 : counts.flagged_mismatches_unread}
          variant="rose"
        />,
        "/content-moderation",
        <Shield {...iconProps} />,
        undefined,
        undefined,
        {
          className:
            location.pathname !== "/content-moderation" && counts.flagged_mismatches_unread > 0
              ? "sidebar-item-has-badge sidebar-item-has-badge--rose"
              : undefined,
        }
      ),
      getItem(
        <Link to="/intelligence-feed">Intelligence Feed</Link>,
        "/intelligence-feed",
        <MessageSquare {...iconProps} />
      ),
    ], "group"),

    getItem("System Core", "group-system-core", null, [
      getItem(
        <Link to="/payment/revenue">Revenue Intelligence</Link>,
        "/payment/revenue",
        <DollarSign {...iconProps} />
      ),
      getItem(
        <Link to="/payment/refunds">Refund Management</Link>,
        "/payment/refunds",
        <RotateCcw {...iconProps} />
      ),
      getItem(
        <Link to="/queue-control">Queue Control</Link>,
        "/queue-control",
        <ListChecks {...iconProps} />
      ),
      getItem(
        <Link to="/payment/subscriptions-plans">Sub Tiers Config</Link>,
        "/payment/subscriptions-plans",
        <CreditCard {...iconProps} />
      ),
      getItem(
        <Link to="/brand-status">Brand Status</Link>,
        "/brand-status",
        <Building2 {...iconProps} />
      ),
      getItem(
        <Link to="/linkedin-dispatch">LinkedIn Dispatch</Link>,
        "/linkedin-dispatch",
        <Linkedin {...iconProps} />
      ),
    ], "group"),

    getItem("Operations", "group-operations", null, [
      getItem(
        <Link to="/brand-management">Brand Management</Link>,
        "/brand-management",
        <ShieldCheck {...iconProps} />
      ),
      getItem(
        <Link to="/admin/cms-pages">Information CMS</Link>,
        "/admin/cms-pages",
        <FileText {...iconProps} />
      ),
      getItem(
        <Link to="/admin/email-templates">Email Templates</Link>,
        "/admin/email-templates",
        <Mail {...iconProps} />
      ),
      getItem(
        <Link to="/admin/staff-access">Staff Access</Link>,
        "/admin/staff-access",
        <Shield {...iconProps} />
      ),
      // getItem(
      //   <Link to="/admin-reports">Admin Reports</Link>,
      //   "/admin-reports",
      //   <BarChart3 {...iconProps} />
      // ),
      // getItem(
      //   <Link to="/notification-center">Notifications</Link>,
      //   "/notification-center",
      //   <Bell {...iconProps} />
      // ),
    ], "group"),

    getItem("Growth Hook", "group-growth", null, [
      getItem(
        <Link to="/admin/growth/twitter-leads">Twitter Leads</Link>,
        "/admin/growth/twitter-leads",
        <Twitter {...iconProps} />
      ),
    ], "group"),
  ];

  // Filter items based on permissions
  const filteredItems = items.map(group => {
    if (group.type === "group" && group.children) {
      const allowedChildren = group.children.filter(child => isRouteAllowed(child.key));
      if (allowedChildren.length === 0) return null;
      return { ...group, children: allowedChildren };
    }
    return isRouteAllowed(group.key) ? group : null;
  }).filter(Boolean);

  const adminSession = JSON.parse(localStorage.getItem('admin_session') || '{}');
  const userEmail = adminSession?.user?.email || adminSession?.session?.user?.email || 'Admin';
  const userInitials = userEmail.substring(0, 2).toUpperCase();



  const renderSidebarContent = () => (
    <div className="custom-sidebar-container" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--color-bg-dark)' }}>
      <div style={{ padding: '0 24px', height: '80px', display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logo192.png" alt="TellBrandz" style={{ width: '40px', height: '40px', borderRadius: '30%' }} />

          {(!collapsed || isMobile) && (
            <div style={{ animation: 'fadeIn 0.3s' }}>
              <h1 style={{ color: 'white', fontWeight: 900, fontSize: '18px', margin: 0, lineHeight: 1, letterSpacing: '-0.05em', fontStyle: 'italic', textTransform: 'uppercase' }}>TellBrandz</h1>
              <p style={{ color: '#FACC15', fontWeight: 900, fontSize: '10px', margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Master Engine</p>
            </div>
          )}
        </div>

        {!isMobile && (
          <Button
            type="text"
            onClick={toggleCollapsed}
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              right: '0px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}
            icon={collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          />
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }} className="custom-scroll">
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={[location.pathname]}
          selectedKeys={[location.pathname]}
          items={filteredItems}
          onClick={({ key }) => {
            navigate(key);
            if (isMobile) {
              setDrawerOpen(false);
            }
          }}
        />
      </div>

      <div style={{ padding: '24px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {(!collapsed || isMobile) ? (
          <div style={{ animation: 'fadeIn 0.3s' }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '16px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                border: '1px solid rgba(255,255,255,0.1)',
                cursor: 'pointer'
              }}
              onClick={() => {
                navigate('/user-profile');
                if (isMobile) setDrawerOpen(false);
              }}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#FACC15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: 'black', fontSize: '11px' }}>
                {userInitials}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{ color: 'white', fontSize: '12px', fontWeight: 900, fontStyle: 'italic', margin: 0, whiteSpace: 'nowrap' }}>{userEmail.split('@')[0]}</p>
                <p style={{ color: '#FACC15', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', margin: 0, letterSpacing: '0.05em' }}>{roleDisplay}</p>
              </div>
            </div>

            <Button
              block
              type="text"
              icon={<LogOut size={16} />}
              style={{
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'uppercase',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: '12px',
                gap: '12px'
              }}
              onClick={() => {
                navigate('/logout');
                if (isMobile) setDrawerOpen(false);
              }}
            >
              Logout
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <div
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#FACC15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: 'black', fontSize: '11px', cursor: 'pointer' }}
              title={roleDisplay}
              onClick={() => navigate('/user-profile')}
            >
              {userInitials}
            </div>
            <Button
              type="text"
              icon={<LogOut size={18} />}
              style={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => navigate('/logout')}
              title="Logout"
            />
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        closable={false}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        styles={{
          body: { padding: 0, background: 'var(--color-bg-dark)' }
        }}
        bodyStyle={{ padding: 0, background: 'var(--color-bg-dark)' }}
      >
        {renderSidebarContent()}
      </Drawer>
    );
  }

  return (
    <Sider
      id="sidebar-layout"
      trigger={null}
      collapsible
      collapsed={collapsed}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        setCollapsed(broken);
      }}
      width={280}
      collapsedWidth={100}
      className="custom-sidebar"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000
      }}
    >
      {renderSidebarContent()}
    </Sider>
  );
};

export default withRouter(SidebarLayout);
